import type { ClaimTask } from '../types/task'

export type TaskUrgency = 'overdue' | 'nearlyDue' | 'none'

const DAY_MS = 24 * 60 * 60 * 1000

// MC1's terminal task statuses, lower-cased. The status arrives trimmed from claimsApi.
const FINISHED_STATUSES = new Set(['closed', 'cancelled'])

// Date-only value in UTC so time-of-day and timezone never shift the comparison.
function toUtcDay(value: string | Date): number | null {
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return null
  return Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate())
}

// Urgency per MSD-683, always measured against the target date:
//
//   overdue    — the target date is before today.
//   nearly due — the target date is today or tomorrow ("due today or 1 day prior").
//   none       — anything later, or no parseable date.
//
// Comparisons are date-only in UTC, so time of day and timezone never shift them.
// A Closed or Cancelled task is finished, so it is never urgent whatever its target date.
// Holding a task does not move its target date, so a held task still shows the border its
// target date earns.
// `today` is injectable for tests.
export function getTaskUrgency(task: ClaimTask, today: Date = new Date()): TaskUrgency {
  if (FINISHED_STATUSES.has(task.status.toLowerCase())) return 'none'

  const due = toUtcDay(task.targetDate)
  const now = toUtcDay(today)
  if (due == null || now == null) return 'none'

  if (due < now) return 'overdue'
  if (due === now || due === now + DAY_MS) return 'nearlyDue'
  return 'none'
}
